import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { BgRecentVisitsService, BgRecentVisit } from '../../services/bg-recent-visits.service';
import { TopicService } from 'src/app/features/user/components/topic/services/topic.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Input() user: any;
  @Input() isLoggedIn = false;
  @Output() close = new EventEmitter<void>();

  isProfileExpanded = false;
  clientImages: string[] = [];
  currentSlide = 0;
  private slideInterval: Subscription | null = null;
  recentVisits: BgRecentVisit[] = [];
  unreadMessagesCount = 0;
  private messageCheckInterval: Subscription | null = null;

  activities = [
    {
      type: 'new-sale',
      title: 'New Sale',
      details: 'Richard Jones has purchased a blue t-shirt for $79',
      icon: 'shopping_cart'
    },
    {
      type: 'withdrawal',
      title: 'Withdrawal Initiated',
      details: 'Your request for withdrawal of $2500 has been initiated.',
      icon: 'logout'
    },
    {
      type: 'question',
      title: 'Question Received',
      details: 'Jane Davis has posted a new question about your product.',
      icon: 'help'
    },
    {
      type: 'comment',
      title: 'Comment Received',
      details: 'Clare Smith has updated your store along with a comment.',
      icon: 'comment'
    }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private recentVisitsService: BgRecentVisitsService,
    private topicService: TopicService
  ) { }

  ngOnInit(): void {
    this.loadClientImages();
    this.startImageSlider();

    // Subscribe to recent visits
    this.recentVisitsService.recentVisits$.subscribe(visits => {
      this.recentVisits = visits;
    });

    // Check for messages immediately if logged in
    if (this.authService.isLoggedIn()) {
      this.updateUnreadMessagesCount();
    }

    // Subscribe to login state changes
    this.authService.getCurrentUser().subscribe(user => {
      if (user) {
        // User is logged in, check messages immediately
        this.updateUnreadMessagesCount();

        // Start interval for checking messages every 10 minutes
        if (!this.messageCheckInterval) {
          this.messageCheckInterval = interval(10 * 60 * 1000).subscribe(() => {
            this.updateUnreadMessagesCount();
          });
        }
      } else {
        // User is logged out, clear interval if it exists
        if (this.messageCheckInterval) {
          this.messageCheckInterval.unsubscribe();
          this.messageCheckInterval = null;
        }
        this.unreadMessagesCount = 0;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.slideInterval) {
      this.slideInterval.unsubscribe();
    }
    if (this.messageCheckInterval) {
      this.messageCheckInterval.unsubscribe();
    }
  }

  loadClientImages(): void {
    const clientData = this.authService.getClientDataStructure();
    if (clientData && clientData.projectStructure) {
      const images: string[] = [];
      this.collectNodeImages(clientData.projectStructure, images);

      // If we have any images, use them (even if less than 3)
      if (images.length > 0) {
        // If we have more than 3 images, take 3 random ones
        if (images.length >= 3) {
          this.clientImages = this.getRandomImages(images, 3);
        } else {
          // If we have less than 3 images, use all of them
          this.clientImages = images;
        }
      } else {
        // If no images were found, use the default image
        this.clientImages = [];
      }
    } else {
      // If no client data or project structure, use the default image
      this.clientImages = [];
    }
  }

  collectNodeImages(nodes: any[], images: string[]): void {
    if (!nodes || !nodes.length) return;

    for (const node of nodes) {
      if (node.img) {
        images.push(node.img);
      }
      if (node.nodes && node.nodes.length) {
        this.collectNodeImages(node.nodes, images);
      }
    }
  }

  getRandomImages(images: string[], count: number): string[] {
    const result: string[] = [];
    if (images.length === 0) return result;

    // Copy array to avoid modifying original
    const imagesCopy = [...images];

    // Get up to 'count' random images
    for (let i = 0; i < Math.min(count, images.length); i++) {
      const randomIndex = Math.floor(Math.random() * imagesCopy.length);
      result.push(imagesCopy[randomIndex]);
      // Remove selected image to avoid duplicates
      imagesCopy.splice(randomIndex, 1);
    }

    return result;
  }

  startImageSlider(): void {
    // Change slide every 5 seconds
    this.slideInterval = interval(5000).subscribe(() => {
      if (this.clientImages.length > 1) {
        this.currentSlide = (this.currentSlide + 1) % this.clientImages.length;
      }
    });
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  closeMenu(): void {
    this.isOpen = false;
    this.close.emit();
  }

  toggleProfile(): void {
    this.isProfileExpanded = !this.isProfileExpanded;
  }

  logoutUser(): void {
    this.authService.logout().subscribe((res) => {
      this.closeMenu();
      if (!res.success) {
        this.router.navigate(['/home']);
      }
    });
  }

  hasUserManagementAccess(): boolean {
    return this.authService.hasRoles('CUSTOMER_ADMIN', 'ADMIN');
  }

  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  hasServicePermission(permission: string): boolean {
    return this.authService.hasServicePermission(permission);
  }

  isAdminPage(): boolean {
    return this.router.url.indexOf('/admin') >= 0;
  }

  private updateUnreadMessagesCount() {
    if (!this.authService.isLoggedIn()) return;
    this.topicService.checkNewMessages().subscribe({
      next: (result) => {
        // Get current user email first
        this.authService.getCurrentUser().subscribe(user => {
          if (!user) return;

          // Sum all unread messages across topics
          this.unreadMessagesCount = Object.values(result)
            .map((messages: any[]) => messages.filter((msg: any) =>
              !msg.readBy || !msg.readBy.includes(user.id)
            ).length)
            .reduce((sum: number, count: number) => sum + count, 0);
        });
      },
      error: () => {
        this.unreadMessagesCount = 0;
      }
    });
  }

}
